'use client';

import type { ReactNode } from 'react';
import { tv } from '../_lib/utils';
import { isValidRichText } from '../_lib/isValidRichText';
import { Anchor } from '../Anchor';
import { IconResolver, type IconType } from '../IconResolver';
import { iconSelectionToPhosphorus } from '../IconSelection';
import type { leftCenterValues } from '../_lib/designTypesStore';
import { Text } from '../Text';

const styles = tv({
	slots: {
		base: 'border border-border-primary group relative p-6 rounded-lg flex flex-col gap-3 overflow-hidden transition-all duration-normal ease-smooth h-full',
	},
	variants: {
		align: {
			left: {
				base: 'items-left',
			},
			center: {
				base: 'items-center text-center',
			},
		},
		hasLink: {
			true: {
				base: 'hover:bg-card-background-tertiary-hover',
			},
		},
	},
});

export type IconCardProps = {
	align?: (typeof leftCenterValues)[number];
	copy?: ReactNode;
	className?: string;
	href?: string;
	icon?: IconType;
	title: ReactNode;
	id?: string;
	learnMore?: {
		learn: string;
		more: string;
	};
};

export function IconCard(props: IconCardProps) {
	const align = props.align ?? 'left';
	const hasLink = props.href ? true : false;
	const { base } = styles({ align, hasLink });

	return (
		<article className={base({ className: props.className })}>
			{props.icon && (
				<div className={`w-6 text-text-brand`}>
					<IconResolver
						className={`w-[1.5rem]! h-[1.5rem]!`}
						icon={typeof props.icon === 'string' ? iconSelectionToPhosphorus(props.icon) : props.icon}
					/>
				</div>
			)}

			<div className={`flex flex-col gap-2`}>
				{hasLink ? (
					<Anchor
						className={`before:bottom-0 before:left-0 before:right-0 before:top-0 before:absolute before:content[""] before:z-10`}
						href={props.href}
					>
						<Text as='h2' styleType='heading-3xs'>
							{props.title}
						</Text>
					</Anchor>
				) : (
					<Text as='h2' styleType='heading-3xs'>
						{props.title}
					</Text>
				)}
				{isValidRichText(props.copy) && (
					<Text className={'opacity-80'} styleType='editorial-md'>
						{props.copy}
					</Text>
				)}
			</div>
		</article>
	);
}
